import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { FC, Fragment } from 'react';

import AlertCard from '@/components/app/alertCard/AlertCard';
import { AlertCardStatus } from '@/components/app/alertCard/AlertCard.utils';
import { MobileCardsLoader } from '@/components/myCustomers/common/loaders/Loaders';
import { PAGE_VIEW_TITLE } from '@/utils/gtm';
import { generateProjectId } from '@/utils/tracking';

import { LeadViewTab } from '../../../interfaces';
import { ExternalLeadsProps } from '../interfaces';
import ExternalLeadCard from './externalLeadCard/ExternalLeadCard';
import { CardsContainer, Wrapper } from './ExternalLeadsMobile.styles';
import TablePagination from '@/components/app/tablePagination/TablePagination';
import { PaginationWrapper } from '@/components/buyerbase/Buyer.styles';
import { getSection, getTabUrl } from '../../../LeadView.utils';
import { useExternalLeadViewTable } from '@/hooks/useExternalLeadViewTable/useExternalLeadViewTable';
import {
  getActiveFilters,
  getSectionFilters
} from '../../filters/LeadFilters.utils';
import { leadViewSelector } from '@/components/state/LeadViewState';
import { useRecoilValue } from 'recoil';

const ExternalLeadsMobile: FC<ExternalLeadsProps> = ({
  currentTab,
  isLoading,
  isTeamLead = false,
  opportunities,
  currentPage,
  agentIds,
  areThereOtherPages
}) => {
  const { t } = useTranslation();
  const { leadViewFilters } = useRecoilValue(leadViewSelector);

  const { getPageCount, getState } = useExternalLeadViewTable({
    currentTab,
    isTeamLead,
    opportunities,
    pageIndex: currentPage - 1
  });

  const { pageIndex } = getState().pagination;

  const section = getSection(currentTab);
  const sectionFilters = getActiveFilters(
    getSectionFilters(section, leadViewFilters)
  );

  if (isLoading) {
    return (
      <Wrapper data-test="mobile-loader">
        <MobileCardsLoader />
      </Wrapper>
    );
  }

  return (
    <Fragment>
      {currentTab === LeadViewTab.LEADS_ALL_EXTERNAL && (
        <Wrapper>
          <AlertCard
            status={AlertCardStatus.WARNING}
            text={t('externalLeadsAutomaticAttributionTabInformation')}
          />
        </Wrapper>
      )}
      {currentTab === LeadViewTab.LEADS_ALL_EXTERNAL_EXPIRED && (
        <Wrapper>
          <AlertCard
            status={AlertCardStatus.WARNING}
            text={t('externalLeadsExpiredTabInformation')}
          />
        </Wrapper>
      )}
      <CardsContainer data-test="external-leads-mobile-container">
        {opportunities.map(opportunity => (
          <Link
            href={`/external-lead/${opportunity.pigeExternalId}`}
            id={generateProjectId(
              PAGE_VIEW_TITLE.LEADS_EXTERNAL,
              `${opportunity.pigeExternalId}`
            )}
            key={opportunity.pigeExternalId}
          >
            <ExternalLeadCard
              isTeamLead={isTeamLead}
              opportunity={opportunity}
            />
          </Link>
        ))}
      </CardsContainer>
      {areThereOtherPages &&
        currentTab === LeadViewTab.LEADS_ALL_EXTERNAL_EXPIRED && (
          <PaginationWrapper>
            <TablePagination
              idTitle={PAGE_VIEW_TITLE.LEADS}
              pageCount={getPageCount()}
              infinitePaging={
                currentTab === LeadViewTab.LEADS_ALL_EXTERNAL_EXPIRED
              }
              pageIndex={pageIndex}
              pageLink={page =>
                getTabUrl(currentTab, page, agentIds, sectionFilters)
              }
            />
          </PaginationWrapper>
        )}
    </Fragment>
  );
};

export default ExternalLeadsMobile;
